// 2. Third-party library imports
import { App } from 'antd';
import update from 'immutability-helper';
import {
    filter,
    find,
    forEach,
    get,
    includes,
    isEmpty,
    orderBy
} from 'lodash-es';
import { Action } from 'redux';
import { channel } from 'redux-saga';
import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import { I18n } from 'aws-amplify/utils';

// 3. Local imports � Constants
import {
    API_NAME,
    DEFAULT_LOCALE,
    initialPrimaryColor,
    initialSecondaryColor,
    maxAPIRefetchCount,
    refetchAPIDelay
} from '../../config/config';
import {
    CompanyIdAttribute,
    IsOrganisationViewAttribute,
    OrganisationIdAttribute
} from '../../constants/authUserAttributes';

// 4. Local imports � GraphQL queries
import { _queries as queries } from '../../graphql/queries.graphql';

// 5. Local imports � Utility functions
import {
    checkShouldRequestRefetch,
    getRegionConfigFromList,
    getTranslatedText,
    refreshCognitoAttributes,
    removeAppliedFiltersForApiRequest,
    updateLessVariables
} from '../../utils/commonFunctions';
import I18nWrapper from '../../utils/I18nWrapper';

// 6. Local imports � Types & interfaces
import { DynamicObject } from '../../utils/commonInterfaces';
import { CompanyCustomFieldConfigure, CompanyUserRole } from '../companies/types';
import { Organisation } from './types';
import { Company } from '../companies/types';
import type { RootState } from '../storeTypes';

// 7. Local imports � Redux slices/actions
import type { ApplicationState } from '../storeTypes';
import { setLocaleAction } from '../common/commonSlice';
import {
    resetAllCompanyDataTableFiltersAction,
    selectUserCompanyRequestAction
} from '../companies/companySlice';
import {
    clearOrganisationActiveTabData,
    closeCompanyRequestAction,
    getOrganisationCompaniesErrorAction,
    getOrganisationCompaniesForUserErrorAction,
    getOrganisationCompaniesForUserRequestAction,
    getOrganisationCompaniesForUserSuccessAction,
    getOrganisationCompaniesRequestAction,
    getOrganisationCompaniesSuccessAction,
    getOrganisationCustomFieldsFilterListByTypes,
    getOrganisationUserDataErrorAction,
    getOrganisationUserDataRequestAction,
    getOrganisationUserDataSuccessAction,
    getOrganisationUsersErrorAction,
    getOrganisationUsersRequestAction,
    getOrganisationUsersSuccessAction,
    getUserOrganisationsRequestAction,
    getUserOrganisationsSuccessAction,
    loadCurrenciesDataRequestAction,
    removeOrganisationUsersRequestAction,
    saveUserOrganisationRequestAction,
    saveUserOrganisationResponseAction,
    selectUserOrganisationRequestAction,
    selectUserOrganisationSuccessAction,
    setSelectedOrganisationUserIdRequestAction,
    setSelectedOrganisationUserIdSuccessAction
} from './organisationSlice';
import { getCurrentUser } from '../auth/authSlice';
import {
    changeEndpointsAction,
    loginUserAction
} from '../auth/authSlice';

// 8. Local imports � API
import client, { post } from 'src/api';

let refetchCount = 0;

/**
 * Function responsible for getting the organisations the user belongs to from API.
 */
function* handleGetUserOrganisationsRequest() {
    try {
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_ORGANISATIONS_FOR_USER
            }
        );

        const organisationsData = get(
            res.data,
            'GetOrganisationsForUser.Organisations'
        );

        refetchCount = 0;

        yield put(getUserOrganisationsSuccessAction(organisationsData));

        const currentUser: DynamicObject = yield select(getCurrentUser);
        const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';
        const selectedOrganisationId = get(
            currentUser,
            OrganisationIdAttribute
        );

        if (!isEmpty(organisationsData) && isOrgView) {
            if (!selectedOrganisationId) {
                yield put(
                    selectUserOrganisationRequestAction(organisationsData[0])
                );
            }
            else {
                const currentOrganisationData = find(
                    organisationsData,
                    (userOrganisationData: Organisation) =>
                        selectedOrganisationId ===
                        userOrganisationData.OrganisationId
                );

                const selectedOrganisationData =
                    currentOrganisationData || organisationsData[0];

                yield put(
                    selectUserOrganisationRequestAction(
                        selectedOrganisationData
                    )
                );
            }
        }
        else if (isOrgView) {
            if (
                isEmpty(
                    filter(organisationsData, [
                        'OrganisationId',
                        selectedOrganisationId,
                    ])
                )
            ) {
                const currentCompanyIdUsed = get(
                    currentUser,
                    CompanyIdAttribute
                );

                const companiesData: CompanyUserRole[] = yield select(
                    (state: ApplicationState) => state.companies.userCompanies
                );

                if (!currentCompanyIdUsed) {
                    yield put(selectUserCompanyRequestAction(companiesData[0]));
                }
                else {
                    const currentCompanyData = find(
                        companiesData,
                        (userCompanyData: CompanyUserRole) =>
                            currentCompanyIdUsed ===
                            userCompanyData.Company.CompanyId
                    );

                    const selectedCompanyData =
                        currentCompanyData || companiesData[0];

                    if (!isEmpty(selectedCompanyData)) {
                        yield put(
                            selectUserCompanyRequestAction(selectedCompanyData)
                        );
                    }
                }
            }
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getUserOrganisationsRequestAction());
        }
        else {
            yield put(getUserOrganisationsSuccessAction([]));
        }
    }
}

const setLanguageAndVocabularies = async (language: string) => {
    const languageJson = await import(`../../languages/${language}.json`);
    I18nWrapper.putVocabulariesForLanguage(language, languageJson.default[language]);
    I18nWrapper.setLanguage(language);

    //I18n.putVocabulariesForLanguage(language, languageJson);
};

/*
 * Function responsible for triggering the user organisation selected
 * and also handles organisation switching to sync the organisation selected here in UI
 * and the one stored in cognito.
 */
const userOrganisationChannel = channel();
const selectOrganisationChannel = channel();
function* handleSelectUserOrganisationRequest({ payload: sagaPayload }: any) {
    const { organisationObject, callback, modal } = sagaPayload;

    try {
        const currentUser: DynamicObject = yield select(getCurrentUser);
        const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';

        if (!isOrgView) {
            yield put(changeEndpointsAction({}));
        }

        const organisationLocale = get(
            organisationObject,
            'DefaultCurrency.Value',
            DEFAULT_LOCALE
        );

        const companiesData: CompanyUserRole[] = yield select(
            (state: ApplicationState) => state.companies.userCompanies
        );
        const currentCompanyIdUsed = get(
            currentUser,
            CompanyIdAttribute
        );
        const currentCompanyData = find(
            companiesData,
            (userCompanyData: CompanyUserRole) =>
                currentCompanyIdUsed ===
                userCompanyData.Company.CompanyId
        );

        if(currentCompanyData && currentCompanyData.User.LanguageCode) {
            I18n.setLanguage(currentCompanyData.User.LanguageCode);
            localStorage.setItem('language', currentCompanyData.User.LanguageCode);
            setLanguageAndVocabularies(currentCompanyData.User.LanguageCode);

            if (currentCompanyData.User.LanguageCode !== organisationLocale) {
                yield put(setLocaleAction(currentCompanyData.User.LanguageCode));
            }
        } else if (organisationLocale) {
            I18n.setLanguage(organisationLocale);
            localStorage.setItem('language', organisationLocale);
            setLanguageAndVocabularies(organisationLocale);

            const currentOrganisationLocale: DynamicObject = yield select(
                (state: ApplicationState) =>
                    get(
                        state.organisations.selectedUserOrganisation,
                        'DefaultCurrency.Value'
                    )
            );

            if (currentOrganisationLocale !== organisationLocale) {
                yield put(setLocaleAction(organisationLocale));
            }
        }

        const organisationId = organisationObject.OrganisationId;
        const currentOrganisationId = get(currentUser, OrganisationIdAttribute);

        if (!isOrgView || organisationId !== currentOrganisationId) {
            yield call(post, {
                apiName: API_NAME,
                path: '/organisation/switch',
                options: {
                    body: {
                        OrganisationId: organisationId,
                    }
                }
            });

            yield call(
                refreshCognitoAttributes,
                async (_err: any, session: any) => {
                    if (get(session, OrganisationIdAttribute)) {

                        userOrganisationChannel.put(
                            loginUserAction({
                                currentUser: session,
                                isAuth: true,
                            })
                        );

                        selectOrganisationChannel.put(
                            selectUserOrganisationSuccessAction(
                                organisationObject
                            )
                        );
                        
                        selectOrganisationChannel.put(
                            getOrganisationCompaniesForUserRequestAction()
                        );
                    }
                }
            );
            yield all([
                put(resetAllCompanyDataTableFiltersAction()),
                put(clearOrganisationActiveTabData())
            ]);
        } else {
            yield put(selectUserOrganisationSuccessAction(organisationObject));
            yield put(getOrganisationCompaniesForUserRequestAction());
        }

        if (callback) callback();
        // calls for queries if needed
    }
    catch (err) {
        let errMessage = 'Failed to switch organisations!';

        if (err instanceof Error) {
            const statusCode = (err as any).response?.statusCode ?? (err as any).$metadata?.httpStatusCode;
            if (
                statusCode === 405 ||
                includes(err.toString(), 'Request failed with status code 405')
            ) {
                errMessage = 'User not allowed to switch to this organisation!';
            }
        }
        else {
            console.error('An unknown error occured.');
        }

        modal?.error({
            title: getTranslatedText("Error"),
            content: getTranslatedText(errMessage),
            onOk: () => {
                window.location.reload();
            },
            okText: getTranslatedText("OK")
        });
    }
}

/**
 * Function called after all the functions called when switching the organisation has finished.
 * Responsible for syncing the Theme for the organisation.
 */
// eslint-disable-next-line
function* handleSelectUserOrganisationSuccess() {
    try {
        const primaryColorTheme = initialPrimaryColor;
        const secondaryColorTheme = initialSecondaryColor;

        updateLessVariables({
            '@custom-primary-color': primaryColorTheme,
            '@custom-secondary-color': secondaryColorTheme,
        });

        return false;
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the organisation companies list to be used for Organisation's Companies page.
 * @param param0
 */
function* handleGetOrganisationCompaniesRequest({ payload }: any) {
    const errorMessage =
        'Error fetching organisation companies list. Please try again later.';

    try {
        const filters = get(payload, 'filters');
        const includeTotalUser = get(payload, 'includeTotalUser');
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'organisation'
        );

        if (includeTotalUser) {
            cleanFilters.IncludeTotalUser = includeTotalUser;
        }

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_ORGANISATION_COMPANIES,
                variables: {
                    ...cleanFilters
                }
            }
        );

        const { Companies: OrganisationCompanies } = get(
            res.data,
            'GetOrganisationCompanies'
        );

        if (OrganisationCompanies) {
            const responsePayload = {
                data: OrganisationCompanies,
            };

            refetchCount = 0;
            yield put(getOrganisationCompaniesSuccessAction(responsePayload));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getOrganisationCompaniesRequestAction(payload));
        }
        else {
            yield put(getOrganisationCompaniesErrorAction([errorMessage]));
        }
    }
}

/**
 * Function for getting the organisation companies list for user to be used for Organisation's Companies page.
 * @param param0
 */
function* handleGetOrganisationCompaniesForUserRequest() {
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_ORGANISATION_COMPANIES_FOR_USER
            }
        );

        const { Companies: OrganisationCompanies } = get(
            res.data,
            'GetOrganisationCompaniesForUser'
        );

        if (OrganisationCompanies) {
            const responsePayload = {
                data: OrganisationCompanies,
            };

            refetchCount = 0;

            yield put(
                getOrganisationCompaniesForUserSuccessAction(responsePayload)
            );
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getOrganisationCompaniesForUserRequestAction());
        }
        else {
            yield put(getOrganisationCompaniesForUserErrorAction());
        }
    }
}

/**
 * Function called for saving the organisation details - talking to the API.
 */
function* handleSaveUserOrganisationRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/organisation/save',
            options: {
                body: payload
            }
        });

        yield put(saveUserOrganisationResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
        // calls for queries if needed
    }
    catch (err) {
        yield put(saveUserOrganisationResponseAction());


        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for loading the currencies data.
 */
function* handleLoadCurrenciesDataRequest({ payload: sagaPayload }: any) {
    const { fileToUpload, callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/organisation/load/currencies',
            options: {
                body: fileToUpload.preview,
            }
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;
        if (callback) callback(res);
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for getting the organisation custom fields filter list for a specific page.
 */
function* handleGetOrganisationCustomFieldsFilterListByTypes({
    payload: sagaPayload,
}: any) {
    const { customFieldTypes, callback } = sagaPayload;

    try {
        const userCompanies: Company[] = yield select(
            (state: ApplicationState) => state.organisations.userCompanies.data
        );

        const customFieldsByType: CompanyCustomFieldConfigure[] = [];

        forEach (userCompanies, (uc: Company) => {
            forEach(
                get(uc, 'CustomFieldsConfigure'),
                (customField: CompanyCustomFieldConfigure) => {
                    const filteredCF = filter(
                        customFieldsByType,
                        (cft: CompanyCustomFieldConfigure) =>
                            cft.Type === customField.Type &&
                            cft.FieldName === customField.FieldName
                    );

                    if (
                        includes(customFieldTypes, customField.Type) &&
                        isEmpty(filteredCF)
                    ) {
                        customFieldsByType.push(customField);
                    }
                }
            );
        });

        const customFieldsByNumberSorted = orderBy(
            customFieldsByType,
            ['Number'],
            ['asc']
        );

        const sortedCustomFieldsByType = orderBy(
            customFieldsByNumberSorted,
            function (item) {
                return customFieldTypes.indexOf(item.Type);
            }
        );

        const res: DynamicObject = {
            CustomFields: sortedCustomFieldsByType,
        };

        if (callback) {
            res.IsSuccess = true;
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that connects to close company api.
 */
function* handleCloseCompanyRequest({ payload: sagaPayload }: any) {
    const { companyId, isClosed, callback } = sagaPayload;
    const payload = {
        CompanyId: companyId,
        IsClosed: isClosed,
    };

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/company/updatecompanystate',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the organisation users list to be used for Organisation's Users page.
 * @param param0
 */
function* handleGetOrganisationUsersRequest({ payload: sagaPayload }: any) {
    const errorMessage =
        'Error fetching organisation users list. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const { filters, pageSize, currentPage } = sagaPayload;

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USERS_FOR_ORGANISATION,
                variables: {
                    Name: filters.Name,
                    Email: filters.Email,
                    RoleIds: filters.Role,
                    CompanyIds: filters.CompanyIds,
                }
            }
        );

        const { OrganisationUsers } = get(res.data, 'GetUsersForOrganisation');

        if (OrganisationUsers) {
            const responsePayload = {
                data: OrganisationUsers,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: false,
                },
            };

            refetchCount = 0;
            yield put(getOrganisationUsersSuccessAction(responsePayload));
        }
        else {
            yield put(getOrganisationUsersErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getOrganisationUsersRequestAction(sagaPayload));
        }
        else {
            yield put(getOrganisationUsersErrorAction([errorMessage]));
        }
    }
}

/**
 * Function that connects to remove organisation users api.
 */
function* handleRemoveOrganisationUsersRequest({ payload: sagaPayload }: any) {
    const { filter, userIds, excludeUsers, callback } = sagaPayload;
    const payload = {
        Filter: filter,
        UserIds: userIds,
        ExcludeUsers: excludeUsers,
    };

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/organisation/removeuser',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that sets the selected organisation user id in the redux state for reference.
 * @param param0
 */
function* handleSetSelectedOrganisationUserIdRequest({ payload }: any) {
    const { organisationUserId, callback } = payload;
    yield put(setSelectedOrganisationUserIdSuccessAction(organisationUserId));
    callback();
}

/**
 * Function that calls the API for getting the organisation user details based on the given Id.
 * @param param0
 */
function* handleGetOrganisationUserDataRequest({
    payload: { organisationUserId },
}: any) {
    const errorMessage =
        'Error fetching organisation user details. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_USER_DETAILS_FOR_ORGANISATION,
                variables: {
                    UserId: organisationUserId,
                }
            }
        );

        const OrganisationUser = get(res.data, 'GetUserDetailsForOrganisation');

        if (OrganisationUser) {
            const responsePayload = {
                record: OrganisationUser,
            };

            yield put(getOrganisationUserDataSuccessAction(responsePayload));
        }
        else {
            yield put(getOrganisationUserDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getOrganisationUserDataErrorAction([errorMessage]));
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetUserOrganisationsRequest() {
    yield takeLatest(
        getUserOrganisationsRequestAction.type,
        handleGetUserOrganisationsRequest
    );
}

function* watchSelectUserOrganisationRequest() {
    yield takeLatest(
        selectUserOrganisationRequestAction.type,
        handleSelectUserOrganisationRequest
    );
}

function* watchSelectUserOrganisationSuccess() {
    yield takeLatest(
        selectUserOrganisationSuccessAction.type,
        handleSelectUserOrganisationSuccess
    );
}

function* watchSelectOrganisationChannel() {
    while (true) {
        const action: Action<any> = yield take(selectOrganisationChannel);
        yield put(action);
    }
}

function* watchUserOrganisationChannel() {
    while (true) {
        const action: Action<any> = yield take(userOrganisationChannel);
        yield put(action);
    }
}

function* watchGetOrganisationCompaniesRequest() {
    yield takeLatest(
        getOrganisationCompaniesRequestAction.type,
        handleGetOrganisationCompaniesRequest
    );
}

function* watchGetOrganisationCompaniesForUserRequest() {
    yield takeLatest(
        getOrganisationCompaniesForUserRequestAction.type,
        handleGetOrganisationCompaniesForUserRequest
    );
}

function* watchSaveUserOrganisationRequest() {
    yield takeLatest(
        saveUserOrganisationRequestAction.type,
        handleSaveUserOrganisationRequest
    );
}

function* watchLoadCurrenciesDataRequest() {
    yield takeLatest(
        loadCurrenciesDataRequestAction.type,
        handleLoadCurrenciesDataRequest
    );
}

function* watchGetOrganisationCustomFieldsFilterListByTypes() {
    yield takeLatest(
        getOrganisationCustomFieldsFilterListByTypes.type,
        handleGetOrganisationCustomFieldsFilterListByTypes
    );
}

function* watchCloseCompanyRequest() {
    yield takeLatest(
        closeCompanyRequestAction.type,
        handleCloseCompanyRequest
    );
}

function* watchGetOrganisationUsersRequest() {
    yield takeLatest(
        getOrganisationUsersRequestAction.type,
        handleGetOrganisationUsersRequest
    );
}

function* watchRemoveOrganisationUsersRequest() {
    yield takeLatest(
        removeOrganisationUsersRequestAction.type,
        handleRemoveOrganisationUsersRequest
    );
}

function* watchSetSelectedOrganisationUserIdRequest() {
    yield takeLatest(
        setSelectedOrganisationUserIdRequestAction.type,
        handleSetSelectedOrganisationUserIdRequest
    );
}

function* watchGetOrganisationUserDataRequest() {
    yield takeLatest(
        getOrganisationUserDataRequestAction.type,
        handleGetOrganisationUserDataRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* organisationsSaga() {
    yield all([
        fork(watchGetUserOrganisationsRequest),
        fork(watchSelectUserOrganisationRequest),
        fork(watchSelectUserOrganisationSuccess),
        fork(watchSelectOrganisationChannel),
        fork(watchUserOrganisationChannel),
        fork(watchGetOrganisationCompaniesRequest),
        fork(watchGetOrganisationCompaniesForUserRequest),
        fork(watchSaveUserOrganisationRequest),
        fork(watchLoadCurrenciesDataRequest),
        fork(watchGetOrganisationCustomFieldsFilterListByTypes),
        fork(watchCloseCompanyRequest),
        fork(watchGetOrganisationUsersRequest),
        fork(watchRemoveOrganisationUsersRequest),
        fork(watchSetSelectedOrganisationUserIdRequest),
        fork(watchGetOrganisationUserDataRequest),
    ]);
}

export default organisationsSaga;
